import * as React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import Swal from 'sweetalert2';
import { MenuItem, TextField } from '@mui/material';
import { Image } from 'antd';
import toast from 'react-hot-toast';

import { updateSafalEventUser } from '../../../services/Service';
import useUserTimeZone, { getTimeZoneAbbreviation } from '../../../hooks/useUserTimeZone';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 800,
  bgcolor: 'background.paper',
  border: '1px solid #000',
  borderRadius: '8px',
  boxShadow: 24,
  displey: 'flex',
  flexDirection: 'column',
  gap: '12px',
  p: 4,
  maxHeight: '95vh',
  overflow: 'auto',
  '@media (max-width: 640px)': {
    width: '95vw',
  },
};

export default function AddSafalEventTypeModal({ open, onClose, data, isDisplay }) {
  const { formatDate } = useUserTimeZone();
  const [newStatus, setNewStatus] = React.useState('');
  const handleClose = () => {
    onClose();
  };

  const onSubmit = async () => {
    const result = await Swal.fire({
      title: `Are you sure you want to update this?`,
      showCancelButton: true,
      confirmButtonColor: '#19c25f',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      appendTo: 'body',
      customClass: {
        container: 'my-swal', // Add a custom class for styling
      },
    });
    if (!result.isConfirmed) {
      return;
    }

    try {
      const res = await updateSafalEventUser({
        _id: data?._id,
        adminApproval: newStatus,
      });
      if (res.status === 200) {
        toast.success(res.data.message);
        handleClose(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    }
  };

  React.useEffect(() => {
    if (open && data) {
      setNewStatus(data?.adminApproval);
    }
  }, [open, data]);

  const countryCode = data?.user?.country === 'India' ? '+91' : data?.user?.country === 'United States' ? '+1' : '';

  return (
    <Modal open={open}>
      <Box sx={style}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ marginBottom: '8px', fontSize: '28px', fontWeight: 700, color: '#19c25f' }}
          >
            SafalEvent
          </Typography>
          <div style={{ display: 'flex', gap: '8px', maxHeight: '24px', alignItems: 'center' }}>
            <p style={{ fontWeight: 'bold' }}>Status</p>
            <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '8px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #19c25f' }}>
              <span
                style={{
                  color: '#19c25f',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  padding: '4px 8px',
                  fontStyle: 'italic',
                }}
              >
                Event Info
              </span>
              <KeyValueBox title={'Code'} value={data?.code} />
              <KeyValueBox title={'Type'} value={data?.type?.name || data?.customType} alt />
              <KeyValueBox title={'Name'} value={data?.name} />
              <KeyValueBox title={'Date'} value={`${formatDate(data?.date)} ${getTimeZoneAbbreviation()}`} alt />
              <KeyValueBox title={'Description'} value={data?.description} />
              <KeyValueBox title={'Recurring Event'} value={data?.recurringEvent ? 'Yes' : 'No'} />
              {data?.recurringEvent && (
                <>
                  <KeyValueBox title={'Frequency'} value={data?.frequency} alt />
                  <KeyValueBox title={'Interval'} value={data?.interval} alt />
                </>
              )}
              <KeyValueBox title={'Targeted Participants'} value={data?.targetedParticipants || ''} alt />
              <KeyValueBox title={'Venue'} value={data?.venue} />
              <KeyValueBox title={'Budget'} value={data?.budget ? `$ ${data?.budget}` : '$ 0'} alt />
              <KeyValueBox title={'Location Url'} value={data?.locationUrl} />
              {data?.eventImage?.[0] && <KeyValueBox img title={'eventImage'} value={data?.eventImage?.[0]} />}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid #19c25f' }}>
              <span
                style={{
                  color: '#19c25f',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  padding: '4px 8px',
                  fontStyle: 'italic',
                }}
              >
                User Info
              </span>
              <KeyValueBox title={'UserID'} value={data?.user?.userID} />
              <KeyValueBox title={'First Name'} value={data?.user?.firstName} />
              <KeyValueBox title={'Middle Name'} value={data?.user?.middleName} alt />
              <KeyValueBox title={'Last Name'} value={data?.user?.lastName} />
              <KeyValueBox title={'Email'} value={data?.user?.email} />
              <KeyValueBox title={'Phone Number'} value={`${countryCode} ${data?.user?.phoneNo}`} alt />
              <KeyValueBox title={'Country'} value={data?.user?.country} alt />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', marginTop: '12px', justifyContent: 'end' }}>
          <Button
            // disabled={isDisableAll}
            onClick={handleClose}
            sx={{ backgroundColor: '#19c25f', textTransform: 'capitalize', ':hover': { backgroundColor: '#19c25f' } }}
            variant="contained"
          >
            Close
          </Button>
          {newStatus && newStatus !== data?.adminApproval && (
            <Button
              onClick={onSubmit}
              sx={{ backgroundColor: '#19c25f', textTransform: 'capitalize', ':hover': { backgroundColor: '#19c25f' } }}
              variant="contained"
            >
              Update
            </Button>
          )}
        </div>
      </Box>
    </Modal>
  );
}

const KeyValueBox = ({ title, value, alt, valueStyle, img }) => {
  const [visible, setVisible] = React.useState(false);
  return (
    <div
      style={{
        display: 'flex',
        gap: '4px',
        backgroundColor: alt ? 'white' : '#eaffea',
        padding: '4px 8px',
        justifyContent: 'space-between',
      }}
    >
      <p style={{ fontWeight: 'bold' }}>{title}</p>

      {img ? (
        <div>
          <button
            style={{
              backgroundColor: '#19c25f',
              padding: '4px 8px',
              color: 'white',
              borderRadius: '4px',
              border: 'none',
              outline: 'none',
            }}
            onClick={() => setVisible(true)}
          >
            Open
          </button>
          <Image
            hidden
            width={10}
            style={{
              display: 'none',
            }}
            src={value}
            preview={{
              zIndex: 1000000,
              visible,
              onVisibleChange: (value) => {
                setVisible(value);
              },
            }}
          />
        </div>
      ) : (
        <p style={{ textAlign: 'right', ...valueStyle }}>{value}</p>
      )}
    </div>
  );
};
